import CRUDPageWrapper from '@/components/crud-page-wrapper/crud-page-wrapper';
import ErrorComponent from '@/components/error/error-component';
import PasswordEdit from '@/pages/account/password-edit';
import ProfileEdit from '@/pages/account/profile-edit';
import ProfileManagement from '@/pages/account/profile-management';
import CustomerEdit from '@/pages/customer-management/customer-edit';
import CustomerNew from '@/pages/customer-management/customer-new';
import CustomerView from '@/pages/customer-management/customer-view';
import CustomersAll from '@/pages/customer-management/customers-all';
import DealerPricingAll from '@/pages/dealer-price-list-management/dealer-pricing-all';
import { FiUser } from 'react-icons/fi';
import {
  MdListAlt,
  MdOutlinePointOfSale,
  MdOutlinePriceCheck,
} from 'react-icons/md';

import Enquire from '@/pages/summary/enquire';
import Summary from '@/pages/summary/summary';
import { Link, Navigate } from 'react-router-dom';
import QuoteManagement from '@/pages/quote-management/quote-management';
import Quote from '../pages/summary/quote';
import SelectSupplier from '../pages/sales-flow/components/select-supplier/select-supplier';
import SelectProductCategory from '../pages/sales-flow/components/select-product-category/select-product-category';
import SelectProduct from '../pages/sales-flow/components/select-product/select-product';
import Configuration from '../pages/sales-flow/components/configuration/configuration';
import MainSummary from '../pages/summary/main-summary';
import SalesFlowPageWrapper from '../components/sales-flow-page-wrapper/sales-flow-page-wrapper.component';
import getQuoteApi from '../api/get/company/sales-flow/get-quote';
import getQuotingCustomersApi from '../api/get/company/sales-flow/get-quoting-customers';
import getEmailTemplateApi from '../api/get/account/get-email-template';

const ShopAuthRoutes = [
  {
    index: true,
    type: 'dealer',
    element: <Navigate to="sales/select-supplier" />,
    errorElement: <ErrorComponent />,
  },
  {
    fallbackIndex: true,
    permissions: ['price.read'],
    company_type: 'admin',
    element: <Navigate to="supplier-pricing-management" />,
    errorElement: <ErrorComponent />,
  },
  {
    path: 'profile-management',
    id: 'dealer-profile-management',
    errorElement: <ErrorComponent />,
    satisfyAll: false,
    children: [
      {
        index: true,
        element: <Navigate to="profile" />,
      },
      {
        path: 'profile',
        children: [
          {
            path: '',
            element: <ProfileManagement />,
            children: [
              {
                index: true,
                element: <ProfileEdit />,
              },
              {
                index: true,
                path: 'change-password',
                element: <PasswordEdit />,
                id: 'dealer-profile-password',
                satisfyAll: true,
                errorElement: <ErrorComponent />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    name: 'Customer Management',
    path: 'customer-management',
    id: 'customer',
    sidebar: true,
    type: 'dealer',
    permissions: ['company.read'],
    sidebarIcon: FiUser,
    active: 'customerManagement',
    errorElement: <ErrorComponent />,
    handle: {
      crumb: () => (
        <Link to="/shop/customer-management">Customer Management</Link>
      ),
    },
    children: [
      {
        path: '',
        id: 'customer-all',
        permissions: ['customer.read'],
        element: <CustomersAll />,
        errorElement: <ErrorComponent />,
      },
      {
        name: 'Customer Management',
        path: '',
        id: 'customer-crud',
        element: <CRUDPageWrapper />,
        permissions: ['company.read'],
        active: 'companyManagement',
        errorElement: <ErrorComponent />,
        children: [
          {
            path: 'new',
            element: <CustomerNew />,
            id: 'customer-new',
            permissions: ['customer.create'],
            satisfyAll: true,
            errorElement: <ErrorComponent />,
            handle: {
              crumb: () => <span>Create New Customer</span>,
            },
          },
          {
            path: 'edit/:customerId',
            element: <CustomerEdit />,
            id: 'customer-edit',
            permissions: ['customer.update'],
            satisfyAll: true,
            errorElement: <ErrorComponent />,
            handle: {
              crumb: () => <span>Edit Customer</span>,
            },
          },
          {
            path: 'view/:customerId',
            element: <CustomerView />,
            id: 'customer-view',
            permissions: ['customer.read'],
            satisfyAll: true,
            errorElement: <ErrorComponent />,
            handle: {
              crumb: () => <span>View Customer</span>,
            },
          },
        ],
      },
    ],
  },
  {
    path: 'quote-management',
    name: 'Quote Management',
    id: 'quote',
    sidebar: true,
    type: 'dealer',
    permissions: ['quote.read'],
    sidebarIcon: MdListAlt,
    active: 'quoteManagement',
    errorElement: <ErrorComponent />,
    element: <QuoteManagement />,
    handle: {
      crumb: () => <Link to="/shop/quote-management">Quote Management</Link>,
    },
  },
  {
    name: 'Supplier Pricing',
    path: 'supplier-pricing-management',
    id: 'supplierPricing',
    company_type: 'admin',
    type: 'both',
    sidebar: true,
    permissions: ['price.read'],
    sidebarIcon: MdOutlinePriceCheck,
    active: 'supplierPricingManagement',
    errorElement: <ErrorComponent />,
    handle: {
      crumb: () => <span>Price List Management</span>,
    },
    children: [
      {
        path: '',
        id: 'supplierPricing-all',
        permissions: ['price.read'],
        element: <DealerPricingAll />,
        errorElement: <ErrorComponent />,
      },
    ],
  },
  {
    path: 'sales',
    id: 'sales',
    name: 'Configurator',
    element: <SalesFlowPageWrapper />,
    errorElement: <ErrorComponent />,
    sidebarIcon: MdOutlinePointOfSale,
    sidebar: true,
    handle: {
      crumb: () => <Link to="/app/user-management">Sales Management</Link>,
    },
    children: [
      {
        index: true,
        element: <Navigate to="select-supplier" />,
        errorElement: <ErrorComponent />,
      },
      {
        // Step 1
        path: 'select-supplier',
        element: <SelectSupplier />,
        errorElement: <ErrorComponent />,
        satisfyAll: true,
        handle: {
          crumb: () => <span>Select Supplier</span>,
        },
      },
      {
        // Step 2
        name: 'Select Product Category',
        path: 'select-product-category',
        type: 'dealer',
        element: <SelectProductCategory />,
        errorElement: <ErrorComponent />,
      },
      {
        // Step 3
        name: 'Select Product',
        path: 'select-product',
        type: 'dealer',
        element: <SelectProduct />,
        errorElement: <ErrorComponent />,
      },
      {
        // Step 4
        name: 'Configuration',
        path: 'configuration',
        type: 'dealer',
        element: <Configuration />,
        errorElement: <ErrorComponent />,
      },
      {
        // Step 5
        name: 'Main Summary',
        path: 'main-summary/:id',
        id: 'main-summary',
        type: 'dealer',
        element: <MainSummary />,
        loader: async ({ params, ...rest }) => {
          const { id: quoteId } = params;

          try {
            const [quoteResponseData, customerData, emailTemplateData] =
              await Promise.all([
                getQuoteApi(quoteId), // GET QUOTE, PRICE & DETAILS
                getQuotingCustomersApi(), // GET ALL CUSTOMERS
                getEmailTemplateApi(), // GET EMAIL TEMPLATE
              ]);
            return {
              quoteResponseData,
              customerData,
              emailTemplateData,
            };
          } catch (error) {
            throw new Error('Failed to load data');
          }
        },
        errorElement: <ErrorComponent />,
        children: [
          {
            index: true,
            element: <Navigate to="summary" />,
            errorElement: <ErrorComponent />,
          },
          {
            path: 'summary',
            id: 'summary',
            element: <Summary />,
            errorElement: <ErrorComponent />,
          },
          {
            path: 'quote',
            id: 'summary-quote',
            element: <Quote />,
            errorElement: <ErrorComponent />,
          },
          {
            path: 'enquire',
            id: 'enquire',
            element: <Enquire />,
            errorElement: <ErrorComponent />,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="sales/select-supplier" />,
  },
];

export default ShopAuthRoutes;
